import { Component, inject, input, output, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ButtonModule } from 'primeng/button';
import { TagModule } from 'primeng/tag';
import { TooltipModule } from 'primeng/tooltip';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { DocumentService } from '@workspace-app/shared/services/document.service';

export interface ProcessedLicense {
  id: string;
  licenseId: string;
  licenseNumber: string;
  coaterName: string;
  productionSiteName: string;
  city: string;
  currentExpiryDate: Date;
  newExpiryDate: Date;
  certificateStatus: string; // "Pending", "Processing", "Completed", "Failed"
  certificateError?: string;
  certificateDocumentId?: string;
  glNotificationStatus?: string; // "Sent", "Failed", "Skipped", "NotApplicable"
  glNotificationError?: string;
  glRecipientEmail?: string;
  glName?: string;
  glAbbreviation?: string;
  certificateSentOn?: Date;
}

@Component({
  selector: 'app-processed-licenses-cards',
  standalone: true,
  imports: [CommonModule, ButtonModule, TagModule, TooltipModule, FontAwesomeModule],
  templateUrl: './processed-licenses-cards.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./processed-licenses-cards.component.scss'],
})
export class ProcessedLicensesCardsComponent {
  private documentService = inject(DocumentService);

  licenses = input.required<ProcessedLicense[]>();
  canRetry = input<boolean>(false);
  isRefreshing = input<boolean>(false);

  retryCertificate = output<string>();
  retryGlNotification = output<string>();
  viewCertificate = output<string>();

  certificateStatusLabel(license: ProcessedLicense): string {
    const statusMap: { [key: string]: string } = {
      Completed: 'Certificate Ready',
      Processing: 'Generating...',
      Failed: 'Generation Failed',
      Pending: 'Pending',
    };
    return statusMap[license.certificateStatus] || license.certificateStatus;
  }

  certificateStatusSeverity(
    license: ProcessedLicense
  ): 'success' | 'warn' | 'danger' | 'info' | 'secondary' | undefined {
    const severityMap: { [key: string]: 'success' | 'warn' | 'danger' | 'info' | 'secondary' } = {
      Completed: 'success',
      Processing: 'warn',
      Failed: 'danger',
      Pending: 'info',
    };
    return severityMap[license.certificateStatus] || 'secondary';
  }

  glStatusLabel(license: ProcessedLicense): string {
    if (!license.glNotificationStatus) return 'Not Sent';

    const statusMap: { [key: string]: string } = {
      Sent: 'GL Notified',
      Failed: 'Notification Failed',
      Skipped: 'Not Sent',
      NotApplicable: 'N/A',
    };
    return statusMap[license.glNotificationStatus] || license.glNotificationStatus;
  }

  glStatusSeverity(license: ProcessedLicense): 'success' | 'warn' | 'danger' | 'info' | 'secondary' | undefined {
    if (!license.glNotificationStatus) return 'secondary';

    const severityMap: { [key: string]: 'success' | 'warn' | 'danger' | 'info' | 'secondary' } = {
      Sent: 'success',
      Failed: 'danger',
      Skipped: 'secondary',
      NotApplicable: 'secondary',
    };
    return severityMap[license.glNotificationStatus] || 'secondary';
  }

  glStatusIcon(license: ProcessedLicense): IconProp {
    if (!license.glNotificationStatus) return ['fas', 'minus'];

    const iconMap: { [key: string]: IconProp } = {
      Sent: ['fas', 'check-circle'],
      Failed: ['fas', 'exclamation-triangle'],
      Skipped: ['fas', 'forward'],
      NotApplicable: ['fas', 'minus'],
    };
    return iconMap[license.glNotificationStatus] || ['fas', 'question-circle'];
  }

  glStatusColor(license: ProcessedLicense): string {
    if (!license.glNotificationStatus) return 'var(--text-color-secondary)';

    const colorMap: { [key: string]: string } = {
      Sent: 'var(--green-500)',
      Failed: 'var(--red-500)',
      Skipped: 'var(--text-color-secondary)',
      NotApplicable: 'var(--text-color-secondary)',
    };
    return colorMap[license.glNotificationStatus] || 'var(--text-color-secondary)';
  }

  glStatusTooltip(license: ProcessedLicense): string {
    if (license.glNotificationError) {
      return `GL Notification Failed: ${license.glNotificationError}`;
    }

    if (!license.glNotificationStatus) return 'GL not notified';

    const tooltipMap: { [key: string]: string } = {
      Sent: `GL Notified: ${license.glRecipientEmail || 'Unknown'}`,
      Failed: 'GL Notification Failed',
      Skipped: 'GL Notification Skipped',
      NotApplicable: 'GL Notification Not Applicable',
    };
    return tooltipMap[license.glNotificationStatus] || 'Unknown GL Status';
  }

  onViewCertificate(license: ProcessedLicense): void {
    if (license.certificateDocumentId) {
      this.viewCertificate.emit(license.certificateDocumentId);
    }
  }

  onRetryCertificate(license: ProcessedLicense): void {
    this.retryCertificate.emit(license.id);
  }

  onRetryGlNotification(license: ProcessedLicense): void {
    this.retryGlNotification.emit(license.id);
  }

  showCertificateDownload(license: ProcessedLicense): boolean {
    return license.certificateStatus === 'Completed' && !!license.certificateDocumentId;
  }

  showCertificateRetry(license: ProcessedLicense): boolean {
    return this.canRetry() && license.certificateStatus === 'Failed';
  }

  showGlRetry(license: ProcessedLicense): boolean {
    return this.canRetry() && license.glNotificationStatus === 'Failed';
  }

  showGlSend(license: ProcessedLicense): boolean {
    // Show send button when GL notification was skipped or never sent (for completed certificates)
    // Only show if GL info is loaded (glName present) and status is not "NotApplicable" (no email)
    return (
      this.canRetry() &&
      license.certificateStatus === 'Completed' &&
      license.glNotificationStatus !== 'NotApplicable' &&
      license.glNotificationStatus !== 'Sent' &&
      !!license.glName
    ); // Only show if GL info is loaded
  }

  showGlInfo(license: ProcessedLicense): boolean {
    return license.glNotificationStatus === 'Sent' && !!license.glRecipientEmail;
  }

  getSendGlTooltip(license: ProcessedLicense): string {
    const email = license.glRecipientEmail || '';
    const gl = license.glAbbreviation || license.glName || '';
    if (email && gl) {
      return `Send to ${email} (${gl})`;
    } else if (email) {
      return `Send to ${email}`;
    }
    return 'Send GL notification';
  }
}
